import { Box, Divider, Grid, Stack } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { lazy, Suspense, useCallback, useState, useMemo } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useGeneralFields, useTriggerPoiEditing } from '../../hooks';
import { GeneralFieldsForm } from '../GeneralFieldsForm';
import { TemplateFieldsForm } from '../TemplateFieldsForm';
import { useTemplateFields } from '../../hooks/useTemplateFields';
import {
  useCreatePointOfInterestMutation,
  PoiStatus,
  VisibilityEnum,
  PoiAccessibilityEnum,
  useAllAssembliesForCreatePoiQuery,
  useGetRecommendationsForAssemblyLazyQuery,
  useGetStructureDefaultAssemblyQuery,
  useUpdatePointOfInterestMutation,
  AllPointOfInterestsForDataLoaderDocument,
  PointOfInterestDocument,
  GetAssemblyPointOfInterestsDocument,
  useGetAllTeamsForCreateOrEditPoiQuery,
  GetOneAssemblyForBlistersDocument,
  useAllPaintRegionsForFilterAndSearchPanelQuery,
  PointOfInterestsCountByStatusDocument,
  PointOfInterestTemplateTypeEnum,
  DefectsAreaForCustomBlocksDocument,
} from '@/__generated__/graphql';
import { convertTemplateStateToTemplateData } from '../../utils';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { PoiFormHeading, PoiFormSubHeading } from '../styles';
import { BlisterFormFields } from '../BlisterFormFields';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import * as state from '@/components/Analysis/state';
import { useBlisterData } from '../../hooks/useBlisterData';
import { useMeasurementTool } from '@/components/Analysis/modules/measurementTool';
import { StyledForm } from '../UpdateGeneralFields/styles';
import { SaveCancelFormButtons } from '../SaveCancelFormButtons';
import { saveCancelButtonCommonStyles } from '../../../styles';
import { convertTemplateNameToPoiType, titleCaseSentence, upperSnakeCase } from '@/utils';
import { enumToString, humanizeString } from '@/utils/string';
import { useViewpointPoiHeight } from '@/components/Analysis/Viewer/hooks/useViewpointPoiHeight';
import {
  SceneOptions,
  useSavePoiScreenshot,
} from '@/components/Analysis/modules/pointOfInterest/createOrEdit/useSavePoiScreenshot';
import { paintDiameterSharedState } from '@/components/shared/CalculatedFields/state';
import {
  ADD_POI_LABEL,
  ADD_SCOPE_LABEL,
  BLISTER,
  BLISTER_TYPE,
  CREATING_LABEL,
  PIT,
  PIT_TYPE,
  SAVE_POI_LABEL,
  SAVING_LABEL,
  WORK_SCOPE_LABEL,
} from '@/constants';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import * as cuboidsState from '../../../../../cuboids/state';
import * as customBlocksState from '@/components/Analysis/modules/CustomBlocks/state';
import { measurementsByField } from '@/components/Analysis/modules/measurementTool/state';
import { LinkedPoiEditor } from '../LinkedPoiEditor';
import { useAnalysisCuboidActions } from '@/components/Analysis/modules/cuboids/hooks/useAnalysisCuboidActions';
import { usePoiFlow } from '@/components/Analysis/Insights/InsightsExplorer/PointOfInterests/usePoiFlow';
import { DropDownField } from '@/components/shared/CustomField';
import { PointOfInterestTemplateDocument } from '@/types';
import { InitialiseConfirmationDialog } from '../InitialiseConfirmationDialog';
import { propsToPoiLineMeasurement } from '@/components/Analysis/modules/measurementTool/hooks/utils';
import { useUncertainField } from '../../hooks/useUncertainField';
import { useIsPoiWorkScope } from '@/hooks/useIsPoiWorkScope';

const fieldsContainer = {
  '& > *': { my: '4%' },
};

const BlisterMeasurementLazy = lazy(() =>
  import('../../../../createOrEdit').then(({ BlisterMeasurement }) => ({
    default: BlisterMeasurement,
  }))
);

export const CreatePoi = () => {
  const [editPointOfInterest, setEditPointOfInterest] = useAtom(poiState.editPointOfInterest);
  const allPoiTemplates = useAtomValue(state.allPointOfInterestTemplates);
  const allVoiTemplates = useAtomValue(state.allVoiTemplates);
  const setPaintDiameter = useSetAtom(paintDiameterSharedState);
  const heightToPointOfInterest = useViewpointPoiHeight();
  const { handleSavePoiScreenshot, setSceneOptions } = useSavePoiScreenshot();
  const setAllPOI = useSetAtom(poiState.structurePois);
  const editingCuboids = useAtomValue(cuboidsState.editingCuboids);
  const cuboidLinkedPois = useAtomValue(cuboidsState.cuboidLinkedPois);
  const isCuboidEditorEnabled = useAtomValue(cuboidsState.isCuboidEditorEnabled);
  const setIsAddingWorkscope = useSetAtom(cuboidsState.isAddingWorkscope);
  const setIsCuboidVisibilityBoxEnabled = useSetAtom(cuboidsState.isCuboidVisibilityBoxEnabled);
  const setNominatedBlister = useSetAtom(state.governingValueByAssemblyId);
  const selectedAssembly = useAtomValue(state.selectedAssemblyId);
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const setPoiCounts = useSetAtom(state.poiCountsByType);
  const measurementsMap = useAtomValue(measurementsByField);

  const setManualBlisterHeightExists = useSetAtom(poiState.manualBlisterHeightExists);

  const navbarHeight = useNavbarHeight();

  const { currentTemplate, selectedAssemblyId, setAssemblyId, changePoiTemplate } = usePoiFlow();
  const pointOfInterest = editPointOfInterest?.pointOfInterest;
  const template = editPointOfInterest?.template;
  const isManuallyCreated = editPointOfInterest?.isManuallyCreated;
  const isAutoGenerated = editPointOfInterest?.isAutoGenerated;

  const { cancelEditing } = useTriggerPoiEditing();
  const { stopAddingCuboid } = useAnalysisCuboidActions();

  const onClose = useCallback(() => {
    setPaintDiameter('0');
    cancelEditing();
    stopAddingCuboid();
  }, [cancelEditing, setPaintDiameter, stopAddingCuboid]);

  const poiType = convertTemplateNameToPoiType(template?.name);

  const [getAssemblyRecommendation] = useGetRecommendationsForAssemblyLazyQuery({
    variables: {
      assemblyId: selectedAssemblyId,
    },
    fetchPolicy: 'network-only',
  });

  const { data: teamsData, loading: teamsLoading } = useGetAllTeamsForCreateOrEditPoiQuery();

  const teamOptions = useMemo(() => {
    return teamsData?.allTeams?.map(({ id, name }) => ({
      label: name,
      value: id,
    }));
  }, [teamsData?.allTeams]);
  const setSelectedPointOfInterestId = useSetAtom(poiState.selectedPointOfInterestId);
  const allTemplates = useAtomValue(state.allPointOfInterestTemplates);
  const [loading, setLoading] = useState(false);

  const templateFieldsData = useTemplateFields({
    selectedPoiInfo: pointOfInterest,
    templateFields: template?.fields ?? [],
  });

  const {
    localBlisterData,
    blisterFieldsData,
    pointSelectMessage,
    onMeasure,
    onSelectPoint,
    clearBlisterData,
    handleManualHeightChange,
    handleMeasureIconClick,
    handleChangeControlledField,
  } = useBlisterData(templateFieldsData, poiType);

  const currentSpherical = useAtomValue(state.viewerCurrentSpherical);
  const assemblyId = pointOfInterest?.assembly?.id;
  const structureId = pointOfInterest?.assembly?.structure?.id ?? '';
  const setSelectedSavedBlockId = useSetAtom(customBlocksState.selectedSavedBlockId);
  const { data: assembliesData } = useAllAssembliesForCreatePoiQuery({
    variables: { structureId },
  });

  const { data: structureWithDefaultAssembly } = useGetStructureDefaultAssemblyQuery({
    variables: { structureId },
  });

  const generalFieldsData = useGeneralFields({
    assignee: 'None',
    dueDate: undefined,
    status: PoiStatus.Inreview,
    visibility: VisibilityEnum.Public,
    name: poiType ? humanizeString(poiType.toString(), '_') : '',
    teamIds: [],
    workpackNumber: 'N/A',
    comment: '',
    accessibility: undefined,
  });

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const [handlePoiCreate] = useCreatePointOfInterestMutation({
    refetchQueries: [
      GetAssemblyPointOfInterestsDocument,
      GetOneAssemblyForBlistersDocument,
      PointOfInterestsCountByStatusDocument,
      DefectsAreaForCustomBlocksDocument,
    ],
  });
  const [updatePointOfInterestMutation] = useUpdatePointOfInterestMutation();

  const { inputAdormantHandlerMap, cancelMeasuring } = useMeasurementTool();

  const showBlisterFields = [PIT_TYPE, BLISTER_TYPE].includes(poiType || '');

  const isPoiTypeOfVoi = allVoiTemplates.some(
    (voiTemplate) => voiTemplate.toLowerCase() === enumToString(poiType).toLowerCase()
  );

  const { checkByType } = useIsPoiWorkScope();
  const isWorkScope = checkByType(poiType || '');

  const shouldIncludeCuboids = useMemo(() => {
    return isWorkScope || isPoiTypeOfVoi;
  }, [isWorkScope, isPoiTypeOfVoi]);

  const { data: paintRegionsData } = useAllPaintRegionsForFilterAndSearchPanelQuery({
    variables: { structureId },
    fetchPolicy: 'cache-first',
  });

  const paintRegionOptions = useMemo(() => {
    return (
      paintRegionsData?.allPaintRegions?.map((paintRegion) => ({
        label: paintRegion.name,
        value: paintRegion.id,
      })) || []
    );
  }, [paintRegionsData?.allPaintRegions]);

  const [selectedPaintRegionId, setSelectedPaintRegionId] = useState('');
  const setSelectedPaintRegionIds = useSetAtom(state.selectedFilteredPaintRegionIds);

  const handlePaintRegionChange = useCallback(
    (paintRegionName: string) => {
      const paintRegion = paintRegionsData?.allPaintRegions?.find(
        (region) => region.name === paintRegionName
      );
      setSelectedPaintRegionId(paintRegion?.id ?? '');
    },
    [paintRegionsData?.allPaintRegions]
  );

  const updatePaintRegionFilter = useCallback((paintRegionId: string) => {
    setSelectedPaintRegionIds((prevIds: string[]) => {
      // Only add if it's not already in the list
      if (!prevIds.includes(paintRegionId)) {
        return [...prevIds, paintRegionId];
      }
      return prevIds;
    });
  }, [setSelectedPaintRegionIds]);

  const onPitDropdownFieldChange = useCallback(
    (manualTemplateValue: boolean) => {
      // If the user decides to mark the Blister as PIT
      // update the name of the POI to reflect the change
      generalFieldsData.setState((current) => ({
        ...current,
        // manualTemplateValue being true means the user has checked the checkbox
        // meaning they want to mark the Blister as PIT
        name: manualTemplateValue ? PIT : BLISTER,
      }));
      const newTemplate = allTemplates.find(
        (item) => item.name === (manualTemplateValue ? PIT : BLISTER)
      ) as PointOfInterestTemplateDocument | undefined;

      if (newTemplate) {
        setEditPointOfInterest((current) => ({
          ...current,
          template: newTemplate,
        }));
      }
    },
    [allTemplates, generalFieldsData, setEditPointOfInterest]
  );

  const selectedTemplate = useMemo(() => {
    return allTemplates.find((template) => convertTemplateNameToPoiType(template.name) === poiType);
  }, [allTemplates, poiType]);

  const isMeasurementToolEnabled = useMemo(() => {
    return selectedTemplate?.fields?.some((field) => field.isMeasurementToolEnabled) ?? false;
  }, [selectedTemplate]);

  const handlePointOfInterest = useCallback(
    async (isInAutoPOIInitFlow: boolean) => {
      const templateData = convertTemplateStateToTemplateData(templateFieldsData.state);
      const generalFields = generalFieldsData.state;
      const type = isInAutoPOIInitFlow ? BLISTER_TYPE : template?.name;
      const centerPoint3d = pointOfInterest?.centerPoint3d;
      const cameraLocation = pointOfInterest?.cameraLocation;

      const locationId = currentSpherical?.id;
      const structureDefaultAssembly = structureWithDefaultAssembly?.structure?.defaultAssemblyId;

      const paintRegionIdInput: string | undefined = (() => {
        // This checks if the 'Add to Workscope' flow is active
        // In that, case whatever VOI is being created, originates from
        // the currently selected custom block
        if (
          editPointOfInterest?.customBlockId &&
          selectedTemplate?.type === PointOfInterestTemplateTypeEnum.PaintBlockVolumeOfInterest
        ) {
          return editPointOfInterest.customBlockId;
        }

        return selectedPaintRegionId;
      })();

      // Update paint region filter state if a paint region is selected
      if (paintRegionIdInput) {
        updatePaintRegionFilter(paintRegionIdInput);
      }

      if (showBlisterFields && !editPointOfInterest.hasBlisterMeasurement) {
        showSnackBar('Please Generate Measurement before save', 'error');
        return;
      }
      if (
        !structureId ||
        (!isManuallyCreated && !assemblyId) ||
        !type ||
        !templateData ||
        !generalFields ||
        !poiType
      ) {
        showSnackBar(
          `Error ${
            isInAutoPOIInitFlow ? 'saving' : 'creating'
          } Point Of Interest. Please contact support if the issue persists.`,
          'error'
        );
        return;
      }
      if (isManuallyCreated && !assemblyId && !structureDefaultAssembly) {
        showSnackBar(
          `Error  ${
            isInAutoPOIInitFlow ? 'saving' : 'creating'
          } Point Of Interest. Uncontextualized Assembly not found. Please contact support if the issue persists.`,
          'error'
        );
        return;
      }

      const accessibilityEnum = Object.values(PoiAccessibilityEnum).find(
        (accessibilityValue) =>
          accessibilityValue === upperSnakeCase(generalFieldsData.state.accessibility ?? '')
      );
      const commonInputFields = {
        ...generalFields,
        name: generalFields?.name ?? (isInAutoPOIInitFlow ? titleCaseSentence(type) : ''),
        centerPoint3d,
        cameraLocation,
        structureId,
        assemblyId: assemblyId || structureDefaultAssembly || '',
        paintRegionId: paintRegionIdInput,
        type: poiType,
        templateData,
        templateId: template?.id,
        locationId,
        blister: localBlisterData,
        dueDate: generalFields?.dueDate?.toISOString(),
        workpackNumber: generalFields.workpackNumber,
        ...(accessibilityEnum ? { accessibility: accessibilityEnum } : {}),
        ...(heightToPointOfInterest ? { viewpointRelativeHeight: heightToPointOfInterest } : {}),
        cuboids: shouldIncludeCuboids
          ? editingCuboids.map((cuboid) => ({
              position: cuboid.position,
              rotation: cuboid.rotation,
              scale: cuboid.scale,
            }))
          : undefined,
        linkedPointOfInterestIds: shouldIncludeCuboids
          ? cuboidLinkedPois?.filter((item) => item?.checked)?.map(({ id }) => id)
          : undefined,
      };

      setLoading(true);
      let pointOfInterestId: string | undefined;

      try {
        if (isInAutoPOIInitFlow) {
          await updatePointOfInterestMutation({
            variables: {
              input: {
                pointOfInterestId: editPointOfInterest.pointOfInterest?.id ?? '',
                ...commonInputFields,
                isUninitialized: true,
                lastModifiedFor: 'Initialize Auto POI',
              },
            },
            refetchQueries: [AllPointOfInterestsForDataLoaderDocument, PointOfInterestDocument],
          });
          pointOfInterestId = editPointOfInterest.pointOfInterest?.id;
        } else {
          const response = await handlePoiCreate({
            variables: {
              input: {
                ...commonInputFields,
                lastModifiedFor: 'Initialization',
                lineMeasurement: isMeasurementToolEnabled
                  ? propsToPoiLineMeasurement(measurementsMap)
                  : undefined,
              },
            },
          });

          const newPoi = response.data?.createPointOfInterest;
          if (newPoi && !isManuallyCreated) setAllPOI((previous) => [...previous, newPoi]);
          pointOfInterestId = newPoi?.id;

          // update poiCountsByType for the table in POI tab only
          if (pointOfInterestId && editPointOfInterest.isManuallyCreated) {
            setPoiCounts((currentCounts) => {
              const updatedCounts = new Map(
                currentCounts.map((count) => [count.type, count.count])
              );

              updatedCounts.set(poiType, (updatedCounts.get(poiType) || 0) + 1);
              updatedCounts.set('All', (updatedCounts.get('All') || 0) + 1);

              return Array.from(updatedCounts, ([type, count]) => ({ type, count }));
            });
          }
        }
        cancelMeasuring();
        if (isWorkScope) {
          setSelectedSavedBlockId(pointOfInterestId);
        } else {
          setSelectedPointOfInterestId(pointOfInterestId);
        }
        setPaintDiameter('0');
        setManualBlisterHeightExists(false);
        showSnackBar(
          isAutoGenerated
            ? 'The POI has been saved successfully'
            : `${isWorkScope ? 'Work Scope' : 'Point of interest'} created successfully`,
          'success'
        );

        const sceneOptions: SceneOptions = {
          areSphericalsVisible: false,
          ...(currentSpherical
            ? {
                // only hide point cloud and pois in spherical
                showPointCloudInSpherical: false,
                arePoisVisible: false,
              }
            : {}),
        };

        const prevSceneOptions = setSceneOptions(sceneOptions);

        setIsAddingWorkscope(false);
        setIsCuboidVisibilityBoxEnabled(false);
        onClose();

        // add a delay on the screenshot to ensure the pois and viewpoints are hidden
        setTimeout(() => {
          handleSavePoiScreenshot({
            poiId: pointOfInterestId,
            refetchPoi: true,
          });
          setSceneOptions(prevSceneOptions);
        }, 500);

        if (selectedAssembly) {
          await getAssemblyRecommendation({
            variables: {
              assemblyId: selectedAssembly,
            },
          }).then((assemblyRecommendation) => {
            const governingPOIId =
              assemblyRecommendation.data?.assembly?.recommendation?.governingValue?.pointOfInterest
                ?.id;
            if (governingPOIId) {
              setNominatedBlister((current) => ({
                ...current,
                [selectedAssembly]: {
                  pointOfInterest: {
                    id: governingPOIId,
                  },
                },
              }));
            }
          });
        }
      } catch {
        showSnackBar(
          `Could not ${isAutoGenerated ? 'save' : 'create'} ${
            isWorkScope ? 'Work Scope' : 'POI'
          }. Please try again`,
          'error'
        );
      } finally {
        setLoading(false);
      }
    },
    [
      templateFieldsData.state,
      generalFieldsData.state,
      template?.name,
      template?.id,
      pointOfInterest?.centerPoint3d,
      pointOfInterest?.cameraLocation,
      currentSpherical,
      structureWithDefaultAssembly?.structure?.defaultAssemblyId,
      showBlisterFields,
      editPointOfInterest.hasBlisterMeasurement,
      editPointOfInterest.customBlockId,
      editPointOfInterest.pointOfInterest?.id,
      editPointOfInterest.isManuallyCreated,
      structureId,
      isManuallyCreated,
      assemblyId,
      poiType,
      localBlisterData,
      heightToPointOfInterest,
      shouldIncludeCuboids,
      editingCuboids,
      cuboidLinkedPois,
      selectedTemplate?.type,
      selectedPaintRegionId,
      showSnackBar,
      cancelMeasuring,
      isWorkScope,
      setPaintDiameter,
      setManualBlisterHeightExists,
      isAutoGenerated,
      onClose,
      handleSavePoiScreenshot,
      setSceneOptions,
      setIsAddingWorkscope,
      selectedAssembly,
      updatePointOfInterestMutation,
      handlePoiCreate,
      isMeasurementToolEnabled,
      measurementsMap,
      setAllPOI,
      setPoiCounts,
      setSelectedSavedBlockId,
      setSelectedPointOfInterestId,
      getAssemblyRecommendation,
      setNominatedBlister,
      setIsCuboidVisibilityBoxEnabled,
    ]
  );

  const poiTypeOptions = useMemo(() => {
    const excludedPoiTypes = new Set(['Blister', 'Pit', 'Recommendation']);
    return allPoiTemplates
      .filter(({ name }) => !excludedPoiTypes.has(name))
      .map(({ name }) => name);
  }, [allPoiTemplates]);

  const assemblies = useMemo(
    () => assembliesData?.allAssemblies ?? [],
    [assembliesData?.allAssemblies]
  );

  const handleAssemblyChange = useCallback(
    (assemblyName: string) => {
      const assembly = assemblies.find(({ tagName }) => tagName === assemblyName);
      setAssemblyId(assembly?.id ?? '');
    },
    [assemblies, setAssemblyId]
  );

  const uncertainField = useUncertainField({ dataHook: templateFieldsData });

  const buttonLabel = useMemo(() => {
    if (isWorkScope) {
      return ADD_SCOPE_LABEL;
    }
    if (isAutoGenerated) {
      return SAVE_POI_LABEL;
    }
    return ADD_POI_LABEL;
  }, [isWorkScope, isAutoGenerated]);

  return (
    <Grid container sx={{ height: '100%' }}>
      <InitialiseConfirmationDialog
        open={showConfirmationModal}
        sphericalChanged={currentSpherical?.id !== editPointOfInterest.poiSphericalId}
        handleClose={() => {
          setShowConfirmationModal(false);
        }}
        handleConfirm={async () => {
          setShowConfirmationModal(false);
          await handlePointOfInterest(!!isAutoGenerated);
        }}
      />
      {localBlisterData && (
        <Grid item xs={7}>
          <Suspense>
            <BlisterMeasurementLazy
              poiToAdd={pointOfInterest}
              uncertainField={uncertainField}
              onMeasure={onMeasure}
              pointSelectMessage={pointSelectMessage}
              onSelectPoint={onSelectPoint}
              clearBlisterData={clearBlisterData}
              measureOnLoad={editPointOfInterest.formState === 'Create'}
              handleChangeControlledField={handleChangeControlledField}
            />
          </Suspense>
        </Grid>
      )}
      <Grid item xs={localBlisterData ? 5 : 12}>
        <StyledForm
          onSubmit={async (event) => {
            event.preventDefault();
            if (isAutoGenerated) {
              setShowConfirmationModal(true);
            } else {
              await handlePointOfInterest(!!isAutoGenerated);
            }
          }}
          style={{ borderLeft: '1px solid #ddd' }}
        >
          <Stack
            sx={{ height: `calc(100vh / var(--zoom) - ${navbarHeight}px)`, overflowY: 'auto' }}
          >
            <Box flex={1} mb="8rem">
              <Stack direction="column" sx={{ px: 2 }}>
                <PoiFormHeading variant="h5">Summary</PoiFormHeading>

                <Box sx={fieldsContainer} data-testid="poi-form-container">
                  <PoiFormSubHeading variant="h5">
                    {isWorkScope ? WORK_SCOPE_LABEL : 'POI'} specifications
                  </PoiFormSubHeading>
                  {editPointOfInterest.isManuallyCreated && (
                    <>
                      <DropDownField
                        id="poi-type"
                        data-testid="poi-type-selector"
                        value={currentTemplate}
                        mode="Edit"
                        title="POI Type"
                        options={poiTypeOptions}
                        onFieldValueChange={changePoiTemplate}
                      />
                      <DropDownField
                        id="assemblies"
                        data-testid="assemblies"
                        value={selectedAssemblyId}
                        mode="Edit"
                        title="Select Assembly"
                        optionsLimit={assemblies.length}
                        options={assemblies.map(({ tagName }) => tagName)}
                        onFieldValueChange={handleAssemblyChange}
                      />
                      <DropDownField
                        id="paint-region"
                        data-testid="paint-region-selector"
                        value={
                          paintRegionOptions.find((opt) => opt.value === selectedPaintRegionId)
                            ?.label ?? ''
                        }
                        mode="Edit"
                        title="Paint Block"
                        options={paintRegionOptions.map((opt) => opt.label)}
                        onFieldValueChange={handlePaintRegionChange}
                      />
                    </>
                  )}
                  {showBlisterFields && (
                    <BlisterFormFields
                      blisterData={blisterFieldsData}
                      handleManualHeightChange={handleManualHeightChange}
                      handleMeasureIconClick={handleMeasureIconClick}
                      onFieldChange={onPitDropdownFieldChange}
                      uncertainField={uncertainField}
                    />
                  )}
                  <TemplateFieldsForm
                    template={template}
                    dataHook={templateFieldsData}
                    formState={editPointOfInterest?.formState}
                    inputAdormantHandlerMap={inputAdormantHandlerMap}
                    handleChangeControlledField={handleChangeControlledField}
                  />
                </Box>
              </Stack>

              <Divider sx={{ mt: 4, color: '#B8CADD' }} />

              <Stack direction="column" sx={{ px: 2 }}>
                <Box sx={fieldsContainer}>
                  <PoiFormSubHeading variant="h5" mt={2}>
                    General Information
                  </PoiFormSubHeading>
                  {shouldIncludeCuboids && <LinkedPoiEditor />}
                  <GeneralFieldsForm
                    teamOptions={teamOptions}
                    teamsLoading={teamsLoading}
                    dataHook={generalFieldsData}
                    currentPoiType={isWorkScope ? WORK_SCOPE_LABEL : undefined}
                  />
                </Box>
              </Stack>
            </Box>

            <SaveCancelFormButtons
              cancelButtonLabel="Cancel"
              buttonLabel={buttonLabel}
              loadingLabel={isAutoGenerated ? SAVING_LABEL : CREATING_LABEL}
              onCancel={() => {
                cancelMeasuring();
                onClose();
                setManualBlisterHeightExists(false);
              }}
              loading={loading}
              buttonIcon={<AddCircleIcon />}
              sx={saveCancelButtonCommonStyles.sx}
              saveButtonStyles={saveCancelButtonCommonStyles.saveButtonStyles}
              cancelButtonStyles={saveCancelButtonCommonStyles.cancelButtonStyles}
              saveButtonDisabled={isCuboidEditorEnabled}
            />
          </Stack>
        </StyledForm>
      </Grid>
    </Grid>
  );
};
